import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import '../../styles/Edit.css';
import { updatePurpose, fetchPurposeOptions } from '../../utils/api';
import { BIOBANK_ROUTES } from '../../constants/routes';

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "3px",
    marginLeft: "10px",
    minHeight: "36px",
    textAlign: "left",
    boxShadow: "none",
    cursor: "pointer",
    '&:hover': { borderColor: '#ccc' },
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: "transparent",
    color: "black",
    cursor: "pointer",
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    position: 'absolute',
    zIndex: 999,
    marginBottom: '5px',
    scrollBehavior: 'auto',
    overflowAnchor: 'none',
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    marginTop: "0px",
    marginLeft: "10px",
    width: "calc(100% - 10px)",
    textAlign: "left",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "310px",
    overflowY: "auto",
    padding: 0,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "rgba(80, 0, 0, 0.1)",
    borderRadius: "3px",
    margin: "1px 2px",
    padding: "1px 4px",
    display: "flex",
    alignItems: "center",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "black",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "var(--tamu-maroon)",
    cursor: "default",
    "&:hover": {
      backgroundColor: "rgba(80, 0, 0, 0.2)",
      color: "var(--tamu-maroon)",
    },
  }),
};

const Option = (props) => {
  return (
    <div
      {...props.innerProps}
      className="custom-option"
      style={{
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        backgroundColor: props.isSelected
          ? "rgba(80, 0, 0, 0.1)"
          : "transparent",
        cursor: "pointer",
      }}
    >
      <div
        className="checkbox"
        style={{
          width: "16px",
          height: "16px",
          border: "1px solid var(--tamu-maroon)",
          borderRadius: "3px",
          marginRight: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: props.isSelected
            ? "var(--tamu-maroon)"
            : "transparent",
        }}
      >
        {props.isSelected && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </div>
      {props.label}
    </div>
  );
};

const EditPurpose = ({ userPurposes = [], onSave }) => {
  const [purposeOptions, setPurposeOptions] = useState([]);
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [otherPurpose, setOtherPurpose] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const loadPurposeOptions = async () => {
      try {
        const purposes = await fetchPurposeOptions();
        const options = purposes.map(purpose => ({ value: purpose, label: purpose }));
        if (!options.some(opt => opt.value === "Other")) {
          options.push({ value: "Other", label: "Other" });
        }
        setPurposeOptions(options);
      } catch (error) {
        console.error("Error loading purpose options:", error);
        setError("Failed to load purposes. Please try again.");
      }
    };
    loadPurposeOptions();
  }, []);

  useEffect(() => {
    if (purposeOptions.length > 0 && userPurposes.length > 0) {
      setSelectedPurposes(
        purposeOptions.filter((option) => userPurposes.includes(option.value))
      );
    }
  }, [purposeOptions, userPurposes]);

  const handlePurposeChange = (selectedOptions) => {
    setSelectedPurposes(selectedOptions || []);
  };

  const handleOtherChange = (e) => {
    setOtherPurpose(e.target.value);
  };

  const validate = () => {
    if (selectedPurposes.length === 0) {
      setError("You must select at least one purpose for access.");
      return false;
    }
    if (
      selectedPurposes.some((purpose) => purpose.value === "Other") &&
      !/^[A-Za-z\s]{15,50}$/.test(otherPurpose)
    ) {
      setError(
        "Other purpose must be between 15 and 50 characters and only contain letters and spaces."
      );
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      if (onSave) {
        onSave({
          purposes: selectedPurposes.map((p) => p.value),
          otherPurpose,
        });
      } else {
        try {
          await updatePurpose(selectedPurposes, otherPurpose);
          navigate(BIOBANK_ROUTES.PROFILE);
        } catch (error) {
          console.error("Error saving purposes:", error.message);
          setError("Failed to save purposes. Please try again.");
        }
      }
    }
  };

  const handleCancel = () => {
    navigate(BIOBANK_ROUTES.PROFILE);
  };

  return (
    <div className="edit-wrapper">
      <div className="edit-container">
        <button className="close-btn" onClick={handleCancel}>X</button>
        <div className="edit-header">
          <h2>Edit Purpose for Access</h2>
        </div>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="edit-content">
            <div className="purpose-select">
              <label htmlFor="purpose">Purpose for Access</label>
              <Select
                id="purpose"
                options={purposeOptions}
                isMulti
                value={selectedPurposes}
                onChange={handlePurposeChange}
                className={`purpose-select ${error ? "error-border" : ""}`}
                components={{ Option }}
                styles={customSelectStyles}
              />
            </div>

            {selectedPurposes.some((purpose) => purpose.value === "Other") && (
              <div className="other-field">
                <label htmlFor="otherPurpose">Specify Other</label>
                <input
                  type="text"
                  id="otherPurpose"
                  value={otherPurpose}
                  onChange={handleOtherChange}
                  placeholder="Enter purpose for access (15-50 characters)"
                />
              </div>
            )}
          </div>
          <div className="button-group">
            <button type="submit" className="save-btn">Save</button>
            <button type="button" className="cancel-btn" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPurpose;
